import { getId } from "@companies/shared/utils/id";
import { faClock, faEdit } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NavLink, useNavigate } from "react-router-dom";

import { Time } from "../../../../shared/types/time";
import { useConfirm } from "../../providers/confirm";
import { createTime, updateTime } from "../../services/time";
import { useLocale } from "../../stores/locale";
import { dateFormat } from "../../utils/date-format";
import { extractSurrealId } from "../../utils/extract-surreal-id";
import { firstSecondOfMinute } from "../../utils/first-second-of-minute";
import { isSameDay } from "../../utils/is-same-day";
import { omit } from "../../utils/omit";
import { Button } from "../button";
import { TotalTimer } from "./total-timer";

export function TimeList({
  date,
  times,
  queryParamForEditLink,
  onListChange,
}: {
  date: Date;
  times: Time[];
  queryParamForEditLink?: string;
  onListChange?: () => void;
}) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const t = useLocale((state) => state.locale);

  function isActive(time: Time) {
    return !time.to;
  }

  function startTime(time: Time) {
    (async () => {
      let redirectToNow = false;
      const now = firstSecondOfMinute(new Date());
      if (!isSameDay(now, date)) {
        try {
          await confirm({
            message: t.time.confirmStartDifferentDay,
            header: t.confirmation,
          });
          redirectToNow = true;
        } catch (error) {
          return;
        }
      }
      await createTime({
        ...omit(time, ["id", "from", "to"]),
        from: now,
      });
      if (redirectToNow) {
        navigate(`/time?date=${dateFormat(now)}`);
      } else {
        onListChange?.();
      }
    })();
  }

  function stopTime(time: Time) {
    (async () => {
      await updateTime(getId(time.id), {
        ...time,
        to: firstSecondOfMinute(new Date()),
      });
      onListChange?.();
    })();
  }

  return (
    <>
      {times.map((t) => (
        <div
          key={t.id}
          className={`p-4 min-h-24 flex gap-5 border-b border-gray-200 ${
            isActive(t) ? "bg-gray-100" : ""
          }`}
        >
          <div className="grow flex flex-col">
            <p className="font-bold">{t.project}</p>
            <div>
              {t.task} - <span className="text-gray-500">{t.description}</span>
            </div>
          </div>
          <div className="flex items-center">
            <TotalTimer times={[t]} />
          </div>
          <div className="flex items-center gap-2">
            {isActive(t) ? (
              <Button onClick={() => stopTime(t)}>
                Stop <FontAwesomeIcon icon={faClock} />
              </Button>
            ) : (
              <Button onClick={() => startTime(t)}>
                Start
                <FontAwesomeIcon icon={faClock} />
              </Button>
            )}
            <NavLink
              to={`/time/edit/${extractSurrealId(t.id)}${queryParamForEditLink ? `?${queryParamForEditLink}` : ""}`}
            >
              <Button severity="secondary">
                <FontAwesomeIcon icon={faEdit} />
              </Button>
            </NavLink>
          </div>
        </div>
      ))}
      {times.length > 0 ? (
        <div className="p-4 flex gap-5">
          <div className="grow flex justify-end">
            Total:&nbsp;
            <TotalTimer times={times} />
          </div>
          <div className="w-28" />
        </div>
      ) : (
        <div className="p-4 flex h-96 items-center justify-center">
          <p className="italic">No times logged for this day</p>
        </div>
      )}
    </>
  );
}
